import { WhereConditionDataType } from "../../segments/segment-where-condition";
import {
  CustomerActivityType,
  CustomerCharacteristicType,
  CustomerDataSource,
  SegmentConditionCategory,
} from "./segment-types";

/**
 * Defines data that customer activities may have on them.
 *
 * e.g. an OrderCreated event may have a numeric total order value field
 * A ProductAddedToCart may have a token collection field of the product.
 *
 * Also is reused to define data on the customer itself.
 *
 */
export interface SegmentDataStructure<T extends string> {
  fields: Record<T, SegmentDataStructureField<T>>;
}

export interface SegmentDataStructureField<T extends string> {
  name: string;
  description: string;
  source: CustomerDataSource;
  dataType: WhereConditionDataType;
  category: SegmentConditionCategory;
  type: T;
  baseFieldName?: string;
}

export type SegmentDataStructureType =
  | "customer-characteristic"
  | CustomerActivityType;

type FieldConfig<T extends string> = Omit<
  SegmentDataStructureField<T>,
  "source" | "type"
>;

function createField<T extends string>(
  type: T,
  config: FieldConfig<T>,
  source: CustomerDataSource,
): SegmentDataStructureField<T> {
  return { ...config, type, source };
}

function createDataStructure<T extends string>(
  fields: Record<T, FieldConfig<T>>,
  source: CustomerDataSource,
): SegmentDataStructure<T> {
  const result = {} as Record<T, SegmentDataStructureField<T>>;
  for (const [key, config] of Object.entries(fields) as [T, FieldConfig<T>][]) {
    result[key] = createField(key, config, source);
  }
  return { fields: result };
}

const customerAttributeFields: Record<
  CustomerCharacteristicType,
  FieldConfig<CustomerCharacteristicType>
> = {
  [CustomerCharacteristicType.CUSTOMER_TAGS]: {
    name: "Customer tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "Tags associated with the customer",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CREATED_TIME]: {
    name: "Created time",
    dataType: WhereConditionDataType.DATE_FULL,
    description: "Time when customer was created",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.BIRTHDAY]: {
    name: "Birthday",
    dataType: WhereConditionDataType.DATE_ANNUAL,
    description: "Birthday of the customer",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.SUBSCRIBED_TO_EMAIL]: {
    name: "Is subscribed to email marketing",
    dataType: WhereConditionDataType.BOOLEAN,
    description: "Is the person subscribed to email marketing?",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.CAN_RECEIVE_EMAIL_MARKETING]: {
    name: "Is contactable via email",
    dataType: WhereConditionDataType.BOOLEAN,
    description:
      "Can the person receive email marketing? Includes any customer with a defined email who hasn't unsubscribed.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.SUBSCRIBED_TO_SMS]: {
    name: "Is subscribed to sms marketing",
    dataType: WhereConditionDataType.BOOLEAN,
    description: "Is the person subscribed to SMS marketing?",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.STATIC_SEGMENT_MEMBERSHIP]: {
    name: "Static segment membership",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "Is the person a member of a static segment?",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.RETURN_RATE_COUNT]: {
    name: "Return rate by count",
    dataType: WhereConditionDataType.NUMERIC,
    description:
      "The number of customer returns over the number of customer orders\n\nThis is a number between 0 and 100",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerCharacteristicType.RETURN_RATE_VALUE]: {
    name: "Return rate by value",
    dataType: WhereConditionDataType.NUMERIC,
    description:
      "The total value of returned goods over the total value of the customer's orders\n\nThis is a number between 0 and 100",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerCharacteristicType.COUNTRY]: {
    name: "Country",
    dataType: WhereConditionDataType.TOKEN,
    description: "The country the person is in",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.STATE_PROVINCE]: {
    name: "State",
    dataType: WhereConditionDataType.TOKEN_HIERARCHY,
    description: "The state or province the person is in",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CITY]: {
    name: "City",
    dataType: WhereConditionDataType.TOKEN_HIERARCHY,
    description: "The city the person is in",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.PROXIMITY_TO_CITY]: {
    name: "Distance to city",
    dataType: WhereConditionDataType.CITY_PROXIMITY,
    description: "Distance to the city",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CUSTOM_FIELDS]: {
    name: "Custom field",
    dataType: WhereConditionDataType.CUSTOM_FIELD,
    description: "Custom fields defined for the customer",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.PHONE_NUMBER_AREA_CODE]: {
    name: "Area code",
    dataType: WhereConditionDataType.TOKEN,
    description: "The area code of the customer's phone number",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.ENROLLED_IN_LOYALTY_LION]: {
    name: "Is enrolled in LoyaltyLion loyalty",
    dataType: WhereConditionDataType.BOOLEAN,
    description: "Is the customer enrolled in LoyaltyLion?",
    category: SegmentConditionCategory.LOYALTY_LION,
  },
  [CustomerCharacteristicType.EMAIL_ADDRESS]: {
    name: "Email address",
    dataType: WhereConditionDataType.STRING,
    description: "The customer's email address",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CUSTOMER_NAME]: {
    name: "Customer name",
    dataType: WhereConditionDataType.STRING,
    description: "The customer's name",
    category: SegmentConditionCategory.ACCOUNT,
  },
};

export const CustomerAttributeDataStructure = createDataStructure(
  customerAttributeFields,
  CustomerDataSource.REDO,
) satisfies SegmentDataStructure<CustomerCharacteristicType>;

function createPlatformDataStructures<T extends string>(
  fields: Record<T, FieldConfig<T>>,
  excludeFromCommentSold?: T[],
): {
  shopify: SegmentDataStructure<T>;
  commentSold: SegmentDataStructure<string>;
} {
  const shopify = createDataStructure(fields, CustomerDataSource.SHOPIFY);

  const commentSoldFields = { ...fields };
  if (excludeFromCommentSold) {
    for (const key of excludeFromCommentSold) {
      delete commentSoldFields[key];
    }
  }

  const commentSold = createDataStructure(
    commentSoldFields as Record<string, FieldConfig<string>>,
    CustomerDataSource.COMMENTSOLD,
  );

  return { shopify, commentSold };
}

const commonProductFields = {
  productId: (context: string): FieldConfig<string> => ({
    name: "Product id",
    dataType: WhereConditionDataType.TOKEN,
    description: `The id of the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  productName: (context: string): FieldConfig<string> => ({
    name: "Product name",
    dataType: WhereConditionDataType.TOKEN,
    description: `The name of the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  variantId: (context: string): FieldConfig<string> => ({
    name: "Product variant id",
    dataType: WhereConditionDataType.TOKEN,
    description: `The id of the product variant ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  variantName: (context: string): FieldConfig<string> => ({
    name: "Product variant name",
    dataType: WhereConditionDataType.TOKEN,
    description: `The name of the product variant ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  variantSku: (context: string): FieldConfig<string> => ({
    name: "Product variant SKU",
    dataType: WhereConditionDataType.TOKEN,
    description: `The SKU of the product variant ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  collectionName: (context: string): FieldConfig<string> => ({
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: `The names of the collections ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  productTags: (context: string): FieldConfig<string> => ({
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: `The tags on the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  vendor: (context: string): FieldConfig<string> => ({
    name: "Vendor",
    dataType: WhereConditionDataType.TOKEN,
    description: `The vendor of the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
};

export enum ProductViewedSegmentFields {
  PRODUCT_ID = "product_id",
  PRODUCT_VARIANT_ID = "product_variant_id",
  PRODUCT_NAME = "product_name",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  COLLECTION_NAME = "collection_name",
  PRODUCT_TAGS = "product_tags",
}

const productViewedFields: Record<
  ProductViewedSegmentFields,
  FieldConfig<ProductViewedSegmentFields>
> = {
  [ProductViewedSegmentFields.PRODUCT_ID]: {
    name: "Product id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product variant id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product variant that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_NAME]: {
    name: "Product name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_VARIANT_NAME]: {
    name: "Product variant name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product variant that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_VARIANT_SKU]: {
    name: "Product variant SKU",
    dataType: WhereConditionDataType.TOKEN,
    description: "The SKU of the product variant that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.COLLECTION_NAME]: {
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the collections that the product belongs to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_TAGS]: {
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The tags on the product",
    category: SegmentConditionCategory.SHOPPING,
  },
};

const {
  shopify: ProductViewedShopifyDataStructure,
  commentSold: ProductViewedCommentSoldDataStructure,
} = createPlatformDataStructures(productViewedFields, [
  ProductViewedSegmentFields.PRODUCT_VARIANT_NAME,
]);

export enum ProductAddedToCartSegmentFields {
  PRODUCT_NAME = "product_name",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_ID = "product_id",
  PRODUCT_VARIANT_ID = "product_variant_id",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  PRODUCT_COLLECTION = "product_collection",
  QUANTITY = "quantity",
  CART_SUBTOTAL = "cart_subtotal",
}

const productAddedToCartFields: Record<
  ProductAddedToCartSegmentFields,
  FieldConfig<ProductAddedToCartSegmentFields>
> = {
  [ProductAddedToCartSegmentFields.PRODUCT_NAME]: {
    name: "Product name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_NAME]: {
    name: "Product variant name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_ID]: {
    name: "Product id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product variant id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_SKU]: {
    name: "Product variant SKU",
    dataType: WhereConditionDataType.TOKEN,
    description: "The SKU of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_COLLECTION]: {
    name: "Product collections",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The collections the added product belongs to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.QUANTITY]: {
    name: "Item quantity",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The quantity of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.CART_SUBTOTAL]: {
    name: "Cart subtotal",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The subtotal value of the cart when the product was added",
    category: SegmentConditionCategory.SHOPPING,
  },
};

const {
  shopify: ProductAddedToCartShopifyDataStructure,
  commentSold: ProductAddedToCartCommentSoldDataStructure,
} = createPlatformDataStructures(productAddedToCartFields, [
  ProductAddedToCartSegmentFields.PRODUCT_VARIANT_NAME,
]);

export enum CollectionViewedSegmentFields {
  COLLECTION_ID = "collection_id",
  COLLECTION_NAME = "collection_name",
}

const collectionViewedFields: Record<
  CollectionViewedSegmentFields,
  FieldConfig<CollectionViewedSegmentFields>
> = {
  [CollectionViewedSegmentFields.COLLECTION_ID]: {
    name: "Collection id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the collection that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CollectionViewedSegmentFields.COLLECTION_NAME]: {
    name: "Collection name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the collection that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
};

const {
  shopify: CollectionViewedShopifyDataStructure,
  commentSold: CollectionViewedCommentSoldDataStructure,
} = createPlatformDataStructures(collectionViewedFields);

export enum ReceivedEmailSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum OpenedEmailSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum ClickedEmailSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum ReceivedTextSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum ClickedTextSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}

const marketingActivityDataStructure = createDataStructure(
  {
    campaign: {
      name: "Campaign",
      dataType: WhereConditionDataType.TOKEN,
      description: "The campaign that was received",
      category: SegmentConditionCategory.MARKETING,
    },
    automation: {
      name: "Automation",
      dataType: WhereConditionDataType.TOKEN,
      description: "The automation that was received",
      category: SegmentConditionCategory.MARKETING,
    },
  } as Record<string, FieldConfig<string>>,
  CustomerDataSource.REDO,
);

const ReceivedEmailDataStructure = marketingActivityDataStructure;
const OpenedEmailDataStructure = marketingActivityDataStructure;
const ClickedEmailDataStructure = marketingActivityDataStructure;
const ReceivedTextDataStructure = marketingActivityDataStructure;
const ClickedTextDataStructure = marketingActivityDataStructure;

export enum ReturnProcessedSegmentFields {
  PRODUCT_NAMES = "product_names",
  PRODUCT_VARIANT_NAMES = "product_variant_names",
  PRODUCT_SKUS = "product_skus",
  PRODUCT_ID = "product_id",
  PRODUCT_VARIANT_ID = "product_variant_id",
  COLLECTION_NAMES = "collection_names",
  PRODUCT_TAGS = "product_tags",
  RETURN_TYPE = "return_type",
}

const returnProcessedFields: Record<
  ReturnProcessedSegmentFields,
  FieldConfig<ReturnProcessedSegmentFields>
> = {
  [ReturnProcessedSegmentFields.PRODUCT_NAMES]: {
    name: "Product names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the products on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_VARIANT_NAMES]: {
    name: "Product variant names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the product variants on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_SKUS]: {
    name: "Product SKUs",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The SKUs of the products on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_ID]: {
    name: "Product ID",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The Product IDs on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product Variant ID",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The Product variant IDs on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.COLLECTION_NAMES]: {
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description:
      "The names of the collections the products on the return belong to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_TAGS]: {
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The tags on the products on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.RETURN_TYPE]: {
    name: "Return type",
    dataType: WhereConditionDataType.TOKEN,
    description:
      "The resolution for the returned product. (e.g. refund, exchange)",
    category: SegmentConditionCategory.SHOPPING,
  },
};

const {
  shopify: ReturnProcessedShopifyDataStructure,
  commentSold: ReturnProcessedCommentSoldDataStructure,
} = createPlatformDataStructures(returnProcessedFields, [
  ReturnProcessedSegmentFields.PRODUCT_VARIANT_NAMES,
  ReturnProcessedSegmentFields.RETURN_TYPE,
]);

export enum OrderPlacedSegmentFields {
  ORDER_TOTAL = "order_total",
  ITEM_COUNT = "item_count",
  COLLECTION_ID = "collection_id",
  COLLECTION_NAME = "collection_name",
  PRODUCT_ID = "product_id",
  PRODUCT_NAME = "product_name",
  PRODUCT_VARIANT_ID = "product_variant_id",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  PRODUCT_TAG = "product_tags",
  VENDOR = "vendor",
}

const orderPlacedFields: Record<
  OrderPlacedSegmentFields,
  FieldConfig<OrderPlacedSegmentFields>
> = {
  [OrderPlacedSegmentFields.ORDER_TOTAL]: {
    name: "Order total",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The total value of the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.ITEM_COUNT]: {
    name: "Item count",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The number of items in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.COLLECTION_ID]: {
    name: "Collection ids",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The ids of the collections the products belong to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.COLLECTION_NAME]: {
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the collections the products belong to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_ID]: {
    name: "Product ids",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The ids of the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_NAME]: {
    name: "Product names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product variant ids",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The ids of the product variants in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_NAME]: {
    name: "Product variant names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the product variants in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_SKU]: {
    name: "Product variant SKUs",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The SKUs of the product variants in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_TAG]: {
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The tags on the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.VENDOR]: {
    name: "Vendor",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The vendors of the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
};

const {
  shopify: OrderPlacedShopifyDataStructure,
  commentSold: OrderPlacedCommentSoldDataStructure,
} = createPlatformDataStructures(orderPlacedFields, [
  OrderPlacedSegmentFields.PRODUCT_VARIANT_NAME,
  OrderPlacedSegmentFields.VENDOR,
]);

export enum CheckoutStartedSegmentFields {}

const CheckoutStartedDataStructure: SegmentDataStructure<
  string & CheckoutStartedSegmentFields
> = { fields: {} };

export enum ActiveOnSiteSegmentFields {
  URL_WITHOUT_PARAMS = "urlWithoutParams",
}

const activeOnSiteFields: Record<
  ActiveOnSiteSegmentFields,
  FieldConfig<ActiveOnSiteSegmentFields>
> = {
  [ActiveOnSiteSegmentFields.URL_WITHOUT_PARAMS]: {
    name: "URL",
    dataType: WhereConditionDataType.TOKEN,
    description:
      "The URL that was visited by the customer (excluding query parameters like ?utm_source=...)",
    category: SegmentConditionCategory.SHOPPING,
  },
};

const {
  shopify: ActiveOnSiteShopifyDataStructure,
  commentSold: ActiveOnSiteCommentSoldDataStructure,
} = createPlatformDataStructures(activeOnSiteFields);

export enum SubscribedToBackInStockSegmentFields {
  PRODUCT_ID = "product_id",
  PRODUCT_NAME = "product_name",
  VARIANT_ID = "variant_id",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  COLLECTION_NAME = "collection_name",
  PRODUCT_TAGS = "product_tags",
  VENDOR = "vendor",
}

const subscribedToBackInStockFields: Record<
  SubscribedToBackInStockSegmentFields,
  FieldConfig<SubscribedToBackInStockSegmentFields>
> = {
  [SubscribedToBackInStockSegmentFields.PRODUCT_ID]:
    commonProductFields.productId("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_NAME]:
    commonProductFields.productName("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.VARIANT_ID]:
    commonProductFields.variantId("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_NAME]:
    commonProductFields.variantName("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_SKU]:
    commonProductFields.variantSku("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.COLLECTION_NAME]:
    commonProductFields.collectionName("that the product belongs to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_TAGS]:
    commonProductFields.productTags("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.VENDOR]: commonProductFields.vendor(
    "the customer subscribed to",
  ),
};

const {
  shopify: SubscribedToBackInStockShopifyDataStructure,
  commentSold: SubscribedToBackInStockCommentSoldDataStructure,
} = createPlatformDataStructures(subscribedToBackInStockFields, [
  SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_NAME,
  SubscribedToBackInStockSegmentFields.VENDOR,
]);

export enum TriggeredBackInStockNotificationSegmentFields {
  PRODUCT_ID = "product_id",
  PRODUCT_NAME = "product_name",
  VARIANT_ID = "variant_id",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  COLLECTION_NAME = "collection_name",
  PRODUCT_TAGS = "product_tags",
  VENDOR = "vendor",
}

const triggeredBackInStockNotificationFields: Record<
  TriggeredBackInStockNotificationSegmentFields,
  FieldConfig<TriggeredBackInStockNotificationSegmentFields>
> = {
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_ID]:
    commonProductFields.productId("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_NAME]:
    commonProductFields.productName("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.VARIANT_ID]:
    commonProductFields.variantId("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_NAME]:
    commonProductFields.variantName("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_SKU]:
    commonProductFields.variantSku("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.COLLECTION_NAME]:
    commonProductFields.collectionName("that the product belongs to"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_TAGS]:
    commonProductFields.productTags("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.VENDOR]:
    commonProductFields.vendor("the customer was notified about"),
};

const {
  shopify: TriggeredBackInStockNotificationShopifyDataStructure,
  commentSold: TriggeredBackInStockNotificationCommentSoldDataStructure,
} = createPlatformDataStructures(triggeredBackInStockNotificationFields, [
  TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_NAME,
  TriggeredBackInStockNotificationSegmentFields.VENDOR,
]);

function mergeDataStructuresWithPlatformLabels(
  shopifyStructure: SegmentDataStructure<string>,
  commentSoldStructure: SegmentDataStructure<string>,
  platformAgnosticFields?: string[],
): SegmentDataStructure<string> {
  const mergedFields: Record<string, SegmentDataStructureField<string>> = {};
  const platformAgnosticSet = new Set(platformAgnosticFields || []);

  for (const [key, field] of Object.entries(shopifyStructure.fields)) {
    const typedField = field as SegmentDataStructureField<string>;
    if (platformAgnosticSet.has(key)) {
      mergedFields[key] = typedField;
    } else {
      const newKey = `${key}_shopify`;
      mergedFields[newKey] = {
        ...typedField,
        name: `${typedField.name} (Shopify)`,
        type: newKey,
        baseFieldName: key,
      };
    }
  }

  for (const [key, field] of Object.entries(commentSoldStructure.fields)) {
    const typedField = field as SegmentDataStructureField<string>;
    if (platformAgnosticSet.has(key)) {
      if (!mergedFields[key]) {
        mergedFields[key] = typedField;
      }
    } else {
      const newKey = `${key}_commentsold`;
      mergedFields[newKey] = {
        ...typedField,
        name: `${typedField.name} (CommentSold)`,
        type: newKey,
        baseFieldName: key,
      };
    }
  }

  return { fields: mergedFields };
}

export const segmentDataStructureTypeToDataStructure: Record<
  SegmentDataStructureType,
  SegmentDataStructure<string> | undefined
> = {
  "customer-characteristic": CustomerAttributeDataStructure,
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]:
    ProductViewedShopifyDataStructure,
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]:
    ProductViewedCommentSoldDataStructure,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]:
    ProductAddedToCartShopifyDataStructure,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]:
    ProductAddedToCartCommentSoldDataStructure,
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]: CheckoutStartedDataStructure,
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]:
    CheckoutStartedDataStructure,
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]: OrderPlacedShopifyDataStructure,
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]:
    OrderPlacedCommentSoldDataStructure,
  [CustomerActivityType.RECEIVED_EMAIL]: ReceivedEmailDataStructure,
  [CustomerActivityType.OPENED_EMAIL]: OpenedEmailDataStructure,
  [CustomerActivityType.CLICKED_EMAIL]: ClickedEmailDataStructure,
  [CustomerActivityType.RECEIVED_TEXT]: ReceivedTextDataStructure,
  [CustomerActivityType.CLICKED_TEXT]: ClickedTextDataStructure,
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]:
    ActiveOnSiteShopifyDataStructure,
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]:
    ActiveOnSiteCommentSoldDataStructure,
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]:
    CollectionViewedShopifyDataStructure,
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]:
    CollectionViewedCommentSoldDataStructure,
  [CustomerActivityType.RETURN_PROCESSED]:
    mergeDataStructuresWithPlatformLabels(
      ReturnProcessedShopifyDataStructure,
      ReturnProcessedCommentSoldDataStructure,
      [ReturnProcessedSegmentFields.RETURN_TYPE],
    ),
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]:
    mergeDataStructuresWithPlatformLabels(
      SubscribedToBackInStockShopifyDataStructure,
      SubscribedToBackInStockCommentSoldDataStructure,
    ),
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]:
    mergeDataStructuresWithPlatformLabels(
      TriggeredBackInStockNotificationShopifyDataStructure,
      TriggeredBackInStockNotificationCommentSoldDataStructure,
    ),
};
